/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { UserProfile } from './user-profile';
import { checkLimitExceeded, isActiveSession, removeSessionFlag, addSessionFlag, getActiveSessionCount, navigateToSessionExeededPage } from '../../session-utils';

const HeaderLoginBase = props => {
  const {
    headerTheme,
    enableSSO,
    localizedElementsData = {},
    member,
    memberLoaded,
    sso = {},
    sketchesHost,
    whiteListedUrlParams = [],
    maxSessionsPerUser,
    enableSessionLimit,
  } = props;
  const { signInSignUp = {} } = localizedElementsData;
  const { signIn: localizedSignIn } = signInSignUp;
  const headerType = headerTheme.includes('single-layer') ? 'single-layer' : '';

  useEffect(() => {
    if (enableSSO && !member) updateMember();
  }, [member]);

  const updateMember = async () => {
    const { currentUser } = await import('@quintype/bridgekeeper-js');
    try {
      const { user, error } = (await currentUser()) || {};
      const loginSuccess = Boolean(user?.["verification-status"]);
      let activeSessionCount;
      if (enableSessionLimit) {
        if (loginSuccess) {
          const isSessionActive = isActiveSession(); // if localstorage has a flag indicating a user who already aquired a session
          if (!isSessionActive) { // dont need to make sessions call for a loggedin user who already has aquired a session (this is done to reduce load on sessions api which is not yet redis cached on BK)
            activeSessionCount = await getActiveSessionCount({ sketchesHost })
            const sessionLimitExceeded = checkLimitExceeded({ activeSessionCount, maxSessionsPerUser })
            if (sessionLimitExceeded) navigateToSessionExeededPage({ sketchesHost, whiteListedUrlParams });
            else addSessionFlag()
          } 
        } else removeSessionFlag();
      }
      if (user || error) memberLoaded(true);
      if (loginSuccess) {
        props.updateMember(user);
      }
    } catch (err) {
      console.log(err);
    }
  };

  if (!enableSSO) return null;
  return (
    <UserProfile
      member={member}
      customCssClasses={headerType}
      localizedSignIn={localizedSignIn}
      sso={sso}
      sketchesHost={sketchesHost}
      whiteListedUrlParams={whiteListedUrlParams}
    />
  );
};

HeaderLoginBase.propTypes = {
  headerTheme: PropTypes.string,
  sketchesHost: PropTypes.string,
  enableSSO: PropTypes.bool,
  localizedElementsData: PropTypes.shape({
    buttonLabels: PropTypes.shape({
      signIn: PropTypes.string
    })
  }),
  updateMember: PropTypes.func,
  member: PropTypes.object,
  sso: PropTypes.object
};

const mapStateToProps = state => {
  const { enableLocalization = false, localizedElements = {} } = get(
    state.qt,
    ['config', 'pagebuilder-config', 'general', 'localization'],
    {}
  );
  return {
    headerTheme: get(state.qt, ['config', 'pagebuilder-config', 'header', 'headerStyle'], 'header_1'),
    enableSSO: get(state.qt, ['config', 'pagebuilder-config', 'header', 'enableSSO'], false),
    localizedElementsData: enableLocalization ? localizedElements : {},
    sso: get(state.qt, ['config', 'pagebuilder-config', 'general', 'sso'], {}),
    sketchesHost: get(state.qt, ['config', 'sketches-host']),
    member: get(state, ['member'], null),
    whiteListedUrlParams: get(
      state.qt,
      ['config', 'pagebuilder-config', 'general', 'others', 'whiteListedUrlParams'],
      []
    ),
    enableSessionLimit: get(state.qt, ['config', 'pagebuilder-config', 'sessionLimit', 'enableSessionLimit'], false),
    maxSessionsPerUser: get(state.qt, ['config', 'pagebuilder-config', 'sessionLimit', 'maxSessionsPerUser'], 0),
  };
};

const mapDispatchToProps = dispatch => ({
  updateMember: member => dispatch({ type: 'MEMBER_UPDATED', member }),
  memberLoaded: memberLoaded => dispatch({ type: 'MEMBER_LOADED', memberLoaded })
});

export const HeaderLogin = connect(mapStateToProps, mapDispatchToProps)(HeaderLoginBase);

export { HeaderLoginBase };
